import React, { useState, useEffect } from 'react';
import PageTitle from '../components/PageTitle';
import JobsPost from '../elements/JobsPost';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosClient from '../../services/axiosClient';
import './JobsGrid.css';
import './ModernJobsGrid.css';
import apiClient from '../../shared/services/apiService.js';
import { buildSearchParams } from '../../utils/searchHelpers';

const JobsGrid = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    // Prefer new snake_case params, fallback to legacy ones for compatibility
    const [cat, setCat] = useState(params.get('category_id') || params.get('cat') || '');
    const [q, setQ] = useState(params.get('q') || '');
    const [where, setWhere] = useState(params.get('where') || '');
    const [cityId, setCityId] = useState(params.get('city_id') || params.get('locality_id') || '');
    const [categories, setCategories] = useState([]); // Stocker les catégories
    const [loading, setLoading] = useState(true); // État de chargement
    const [localities, setLocalities] = useState([]); // [{id,name}]
    const [loadingCities, setLoadingCities] = useState(false);
    // Home stats
    const [stats, setStats] = useState({ posts: 0, users: 0, locations: 0 });
    const [statsConfig, setStatsConfig] = useState({
        icon_count_posts: 'fa-solid fa-briefcase',
        icon_count_users: 'fa-solid fa-users',
        icon_count_locations: 'fa-regular fa-map',
        counter_up_delay: 10,
        counter_up_time: 2000,
    });
    const [statsLoading, setStatsLoading] = useState(false);
    const [statsError, setStatsError] = useState('');

    useEffect(() => {
    const fetchCategories = async () => {
        try {
            const response = await axiosClient.get('/categories');
            console.log('Données reçues de l\'API:', response.data);

            if (Array.isArray(response.data.result.data)) {
                setCategories(response.data.result.data); // on prend le bon chemin
            } else {
                console.error('Les données reçues ne contiennent pas un tableau:', response.data);
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des catégories:', error);
        } finally {
            setLoading(false);
        }
    };
    fetchCategories();
}, []);

    // Fetch home stats
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setStatsLoading(true);
                setStatsError('');
                console.log('[JobsGrid] Calling getStats API...');
                const res = await apiClient.get('/homeSections/getStats', {
                    params: { fetchData: 1 },
                    headers: { 'X-Skip-Auth': true },
                });
                console.log('[JobsGrid] Stats API Response:', res?.data);
                const root = res?.data;
                const counts = root?.result?.data?.count || root?.data?.count || root?.count || {};
                const cfg = root?.result?.getStatsOp || root?.getStatsOp || {};
                console.log('[JobsGrid] Parsed counts:', counts);
                if (!mounted) return;
                setStats({
                    posts: Number(counts.posts) || 0,
                    users: Number(counts.users) || 0,
                    locations: Number(counts.locations) || 0,
                });
                setStatsConfig((prev) => ({
                    ...prev,
                    icon_count_posts: cfg.icon_count_posts || prev.icon_count_posts,
                    icon_count_users: cfg.icon_count_users || prev.icon_count_users,
                    icon_count_locations: cfg.icon_count_locations || prev.icon_count_locations,
                    counter_up_delay: Number(cfg.counter_up_delay) || prev.counter_up_delay,
                    counter_up_time: Number(cfg.counter_up_time) || prev.counter_up_time,
                }));
            } catch (e) {
                console.error('[JobsGrid] getStats fetch failed:', e);
                if (mounted) setStatsError(e.message || 'Erreur lors du chargement des statistiques');
            } finally {
                if (mounted) setStatsLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    // Fetch cities/localities for Senegal
    useEffect(() => {
        let mounted = true;
        (async ()=>{
            try {
                setLoadingCities(true);
                const res = await axiosClient.get('/countries/SN/cities?perPage=100', { headers: { Accept: 'application/json' } });
                let data = res?.data;
                let list = data?.result?.data || data?.data || data || [];
                if (!Array.isArray(list) && Array.isArray(list?.data)) list = list.data;
                const mapped = (list||[])
                    .map((c)=>({ id: c?.id ?? c?.city_id ?? c?.value, name: c?.name ?? c?.label }))
                    .filter((c)=>c.id && c.name);
                if (!mounted) return;
                setLocalities(mapped);
            } catch(e) {
                if (process.env.NODE_ENV !== 'production') console.warn('[JobsGrid] cities fetch failed');
            } finally {
                if (mounted) setLoadingCities(false);
            }
        })();
        return ()=>{ mounted = false; };
    }, []);

    function onSubmit(e) {
        e.preventDefault();
        const sp = new URLSearchParams();
        // Use API-friendly params that JobsPost will convert to correct format
        if (cat) sp.set('category_id', String(cat));
        if (q) sp.set('q', q);
        if (cityId) {
            sp.set('city_id', String(cityId));
        } else if (where) {
            const w = String(where).trim();
            sp.set('where', w);
        }
        sp.set('page', '1');
        sp.set('perPage', '10');
        navigate({ pathname: '/posts', search: `?${sp.toString()}` });
    }

    // Quick chips helpers
    function goWith({ qv = q, wherev = where, catv = cat } = {}) {
        const sp = new URLSearchParams();
        if (catv) sp.set('category_id', String(catv));
        if (qv) sp.set('q', qv);
        if (wherev) {
            const w = String(wherev).trim();
            if (/^\d+$/.test(w)) sp.set('locality_id', w); else sp.set('where', w);
        }
        sp.set('page', '1');
        sp.set('perPage', '10');
        navigate({ pathname: '/posts', search: `?${sp.toString()}` });
    }

    function handleQuickWhere(city) {
        setWhere(city);
        goWith({ wherev: city });
    }

    function handleQuickCat(categoryId) {
        setCat(String(categoryId));
        goWith({ catv: String(categoryId) });
    }

    return (
        <div className='page-content modern-jobs-page'>
            {/* Modern Hero Section */}
            <section className="modern-hero-section">
                <div className="hero-background">
                    <div className="hero-pattern"></div>
                    <div className="hero-gradient"></div>
                </div>
                <div className="container">
                    <div className="hero-content">
                        <div className="hero-badge">
                            <i className="fa-solid fa-briefcase"></i>
                            <span>Opportunités d'Appels d'offres</span>
                        </div>
                        <h1 className="hero-title">
                            Découvrez votre
                            <span className="highlight-text"> prochaine opportunité</span>
                        </h1>
                        <p className="hero-subtitle">
                            Explorez des milliers d'Appels d'offres dans tous les secteurs d'activité au Sénégal
                        </p>
                        
                        {/* Modern Search Bar (Pro) */}
                        <div className="modern-search-container">
                          <form className="modern-search-form modern-search-form--pro" onSubmit={onSubmit}>
                            <div className="search-input-group">
                              {/* Query */}
                              <div className="search-field">
                                <i className="fa-solid fa-magnifying-glass search-icon"></i>
                                <input
                                  className="search-input"
                                  value={q}
                                  onChange={(e) => setQ(e.target.value)}
                                  placeholder="Poste, entreprise, mots-clés..."
                                  type="search"
                                  aria-label="Recherche par mots-clés"
                                />
                              </div>

                              {/* Location (city select with optional free text fallback) */}
                              <div className="search-field">
                                <i className="fa-solid fa-location-dot search-icon"></i>
                                <select
                                  className="search-select"
                                  value={cityId}
                                  onChange={(e)=> setCityId(e.target.value)}
                                  aria-label="Ville"
                                  disabled={loadingCities}
                                >
                                  <option value="">{loadingCities ? 'Chargement des villes...' : 'Toutes les localités'}</option>
                                  {localities.map((c)=> (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                  ))}
                                </select>
                              </div>

                              {/* Category */}
                              <div className="search-field">
                                <i className="fa-solid fa-layer-group search-icon"></i>
                                <select
                                  className="search-select"
                                  value={cat}
                                  onChange={(e) => setCat(e.target.value)}
                                  disabled={loading}
                                  aria-label="Catégorie"
                                >
                                  <option value="">
                                    {loading ? 'Chargement...' : 'Toutes les catégories'}
                                  </option>
                                  {categories.length > 0 ? (
                                    categories.map((category) => (
                                      <option key={category.id} value={category.id}>
                                        {category.name}
                                      </option>
                                    ))
                                  ) : (
                                    <option disabled>Aucune catégorie disponible</option>
                                  )}
                                </select>
                              </div>

                              {/* Divider on desktop */}
                              <div className="search-divider d-none d-lg-block" aria-hidden="true"></div>

                              {/* CTA */}
                              <button className="search-button search-button--pro" type="submit">
                                <i className="fa-solid fa-search"></i>
                                <span>Rechercher</span>
                              </button>
                            </div>

                            {/* Quick chips row */}
                            <div className="quick-filters">
                              <span className="quick-label">
                                <i className="fa-regular fa-wand-magic-sparkles"></i> Suggestions rapides:
                              </span>
                              <div className="quick-chips">
                                {/* popular cities */}
                                {localities.slice(0,3).map(city => (
                                  <button key={city.id} type="button" className="quick-chip" onClick={() => { setCityId(String(city.id)); goWith({ wherev: '', catv: cat }); }}>
                                    <i className="fa-solid fa-location-dot"></i> {city.name}
                                  </button>
                                ))}
                                {/* top categories */}
                                {Array.isArray(categories) && categories.slice(0, 4).map(c => (
                                  <button key={c.id} type="button" className="quick-chip" onClick={() => handleQuickCat(c.id)}>
                                    <i className="fa-solid fa-layer-group"></i> {c.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </form>
                        </div>
                        
                        {/* Quick Stats */}
                        <div className="hero-stats">
                            <div className="stat-item">
                                <div className="stat-number">
                                    {statsLoading ? '...' : stats.posts}
                                    {statsError && <small className="text-danger d-block" style={{fontSize: '0.7rem'}}>Erreur</small>}
                                </div>
                                <div className="stat-label">Offres actives</div>
                            </div>
                            <div className="stat-item">
                                <div className="stat-number">
                                    {statsLoading ? '...' : stats.users}
                                </div>
                                <div className="stat-label">Utilisateurs</div>
                            </div>
                            <div className="stat-item">
                                <div className="stat-number">
                                    {statsLoading ? '...' : stats.locations}
                                </div>
                                <div className="stat-label">Localités</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Jobs Listing Section */}
            <section className="modern-jobs-section">
                <div className="container">
                    <JobsPost />
                </div>
            </section>
        </div>
    );
};

export default JobsGrid;